/**
 * Pure resolver for the app-wide active brand id, factored out of
 * `BrandContextProvider` so the fallback rules stay unit-testable.
 *
 * Priority:
 *  1. The just-ADDED brand (`intentBrandId`, from the `brew_brand_intent`
 *     cookie) while it is the org's NEWEST brand and still active — extracting,
 *     or the brief `ready` window before the gate consumes the intent. So "Add
 *     Brand" switches to the new brand IMMEDIATELY (before extraction finishes)
 *     and does not flicker back at the ready flip. `brands` is newest-first
 *     (deleting excluded), so `brands[0]` is the org's newest brand — gating on
 *     it mirrors the server resolvers' newest-brand rule
 *     (`getActiveBrandStatusForOrg`) so SSR and the sidebar never disagree. This
 *     wins even over `resolvedId`: a manual sidebar pick CLEARS the intent
 *     cookie, so an unconsumed intent means no manual switch has overridden the
 *     add yet (the gate clears it on the `extracting → ready` transition; a
 *     `failed` brand is never the newest-active match, so it is ignored).
 *  2. An explicitly selected brand that still exists (`resolvedId`).
 *  3. The newest ready brand — the normal default.
 *  4. First-brand exception: when NO ready brand exists yet but one is
 *     mid-extraction, scope to that seeded brand so the app is usable and
 *     editable while the agentic chat fills it in.
 *  5. Empty string while brands are still loading or nothing qualifies.
 *
 * `brands` is expected newest-first (as `listOrgBrands` returns), so the first
 * match in each `find` is the newest of its status.
 */
export function resolveActiveBrandId(args: {
  resolvedId: string | undefined
  areBrandsLoaded: boolean
  brands: ReadonlyArray<{ _id: string; status: string }>
  /** Just-added brand to activate immediately (the `brew_brand_intent` cookie). */
  intentBrandId?: string | null
}): string {
  const { resolvedId, areBrandsLoaded, brands, intentBrandId } = args
  if (intentBrandId) {
    const newest = brands[0]
    if (
      newest &&
      newest._id === intentBrandId &&
      (newest.status === 'extracting' || newest.status === 'ready')
    ) {
      return newest._id
    }
  }
  if (resolvedId) {
    return resolvedId
  }
  if (!areBrandsLoaded) {
    return ''
  }
  const ready = brands.find((b) => b.status === 'ready')
  const fallback = ready ?? brands.find((b) => b.status === 'extracting')
  return fallback?._id ?? ''
}

/**
 * Should the server-validated brand seed be trusted RIGHT NOW — i.e. is it
 * safe for `isSelectedBrandValid` to promote it before `listOrgBrands`
 * confirms it?
 *
 * Two conditions must hold:
 *  1. The server POSITIVELY validated the seed against this request's
 *     Convex brand reads (`resolveInitialBrand` → `validated: true`).
 *  2. The live Convex JWT is not POSITIVELY mismatched with the server org.
 *     `convexOrgId === undefined` (whoami still loading on a cold
 *     bootstrap) is fine — `useAuthedQuery` holds every query until the
 *     socket authenticates, and that first JWT is minted for the SAME
 *     active org the server rendered, so no cross-org fire is possible.
 *     A DEFINED-but-different claim (including `null`, a personal-session
 *     claim) is the residual org-switch window: the RSC tree has
 *     re-converged on the new org but the socket still holds the previous
 *     org's token. Trusting the seed there would let every brand-scoped
 *     subscription fire with the new org's args against the old token —
 *     `NOT_AUTHORIZED` → a full-shell `OrgAuthSwitchBoundary` flash.
 *     Before the trusted-seed optimization this window was implicitly
 *     covered because `brandId` stayed `''` until the org-GATED
 *     `listOrgBrands` resolved; this predicate preserves that hold while
 *     keeping the cold-load fast path.
 */
export function resolveTrustedSeedId(args: {
  initialBrandId: string | undefined
  isInitialBrandIdValidated: boolean
  /** Live Convex JWT org claim (`useConvexOrgId()`). */
  convexOrgId: string | null | undefined
  /** Server-seeded org id (`OrgContext.orgId`). */
  orgId: string | null | undefined
}): string | undefined {
  const { initialBrandId, isInitialBrandIdValidated, convexOrgId, orgId } = args
  if (!isInitialBrandIdValidated || !initialBrandId) {
    return
  }
  if (!orgId) {
    return
  }
  if (convexOrgId !== undefined && convexOrgId !== orgId) {
    return
  }
  return initialBrandId
}

/**
 * Is the explicitly-selected / server-seeded brand id safe to promote to the
 * app-wide active brand?
 *
 * True when there is no selection (nothing to validate), OR the brands list
 * has loaded AND still contains the selection. While the list is still
 * loading the answer is FALSE — the seed can be an UNVALIDATED cookie echo
 * (see `resolveInitialBrand`), including a legacy `customBrands` id from a
 * different id-space, and promoting it would scope every `v.id('brands')`
 * query to a brand that may not exist for the current org — with ONE
 * exception: when the selection equals `trustedSeedId`, an id the SERVER
 * positively confirmed against this request's Convex brand reads
 * (`resolveInitialBrand` → `validated: true`). Trusting that seed lets every
 * brand-scoped client subscription fire on first paint instead of waiting
 * out the whoami + `listOrgBrands` round-trip; the id-space hazard is
 * excluded by construction (it came from the ready list / live status, not a
 * cookie echo). Once the list loads it is authoritative again — a trusted
 * seed that vanished (e.g. brand deleted mid-flight) is dropped.
 */
export function isSelectedBrandValid(args: {
  selectedBrandId: string | undefined
  areBrandsLoaded: boolean
  brands: ReadonlyArray<{ _id: string; status: string }>
  /** Server-validated seed id, safe to trust before the list loads. */
  trustedSeedId?: string
}): boolean {
  const { selectedBrandId, areBrandsLoaded, brands, trustedSeedId } = args
  if (!selectedBrandId) {
    return true
  }
  if (!areBrandsLoaded) {
    return trustedSeedId !== undefined && selectedBrandId === trustedSeedId
  }
  return brands.some((b) => b._id === selectedBrandId)
}

/**
 * Decide where to go after deleting a brand. The caller passes the current
 * brand list (which may still include the just-deleted id during the reactive
 * delete window) plus that id; we exclude it explicitly and pick the next
 * active brand:
 *
 *   - No brand remains            → `{ kind: 'setup' }` (send the org to /setup).
 *   - A `ready` brand remains     → `{ kind: 'switch' }` to the NEWEST ready one.
 *   - Only extracting brands left → `{ kind: 'switch' }` to the newest of those,
 *                                   so the app stays scoped to a usable brand
 *                                   while it finishes extracting.
 *
 * `brands` is expected newest-first (as `listOrgBrands` returns), so the first
 * match is the newest of its status. Pure + unit-tested so the brand-config
 * delete handler can make the post-delete switch explicit and deterministic
 * rather than relying on passive re-resolution. The server + client brand gates
 * remain the backstop for the zero-brand redirect.
 */
export function resolvePostDeleteBrandTarget(args: {
  brands: ReadonlyArray<{ _id: string; status: string }>
  deletedBrandId: string
}): { kind: 'setup' } | { kind: 'switch'; brandId: string } {
  const remaining = args.brands.filter((b) => b._id !== args.deletedBrandId)
  if (remaining.length === 0) {
    return { kind: 'setup' }
  }
  const ready = remaining.find((b) => b.status === 'ready')
  const next = ready ?? remaining[0]
  return { kind: 'switch', brandId: next._id }
}

/**
 * Is the brand the app is CURRENTLY scoped to ready for email generation?
 *
 * The chat composers key on this — not on `hasReadyBrand` ("some brand in
 * the org is ready") — because a new chat is scoped to the active brand, and
 * that brand may legitimately be `extracting` everywhere else in the app (the
 * app scopes to a brand from the moment its domain is submitted). Submitting
 * against an in-flight brand would have no design system to draw on, and the
 * orchestrator classifies a first turn on a not-ready `brands` row as a
 * brand-extraction run. While `listOrgBrands` is still loading, the server
 * seed (`initialActiveBrandReady`, computed by the shell from its ready-brands
 * read + the seeded brand id) keeps the /home composer enabled from first
 * paint; once loaded, the live status wins.
 */
export function resolveIsActiveBrandReady(args: {
  areBrandsLoaded: boolean
  activeBrandStatus: string | undefined
  initialActiveBrandReady: boolean
}): boolean {
  if (!args.areBrandsLoaded) {
    return args.initialActiveBrandReady
  }
  return args.activeBrandStatus === 'ready'
}
